import React, { useState } from 'react';
import {
    Autocomplete,
    Badge,
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Popover,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { FilterAltOff, FilterList } from '@mui/icons-material';

// Must match the keys of FY_Id in influencer_backend_node/middleware/auth.js
// exactly — the backend resolves this value straight from the x-FY header.
const FINANCIAL_YEARS = ['All', '2025-2026', '2024-2025', '2023-2024', '2022-2023'];

// Values must match VALID_MONTHS in influencer_backend_node/routes/campaign.js's
// /search handler exactly — sent as a two-digit month code, matched against
// the campaign's created-on month (derived from its _id, same as Financial Year).
export const MONTHS = [
    { value: 'All', label: 'All' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
];
export const MONTH_LABELS = Object.fromEntries(MONTHS.map((m) => [m.value, m.label]));

// Values must match NET_REVENUE_FILTERS in influencer_backend_node/routes/campaign.js.
export const NET_REVENUE_OPTIONS = [
    { value: 'All', label: 'All' },
    { value: 'with', label: 'Has Net Revenue' },
    { value: 'without', label: 'No Net Revenue' },
    // Sub-options of "Has Net Revenue" (together they add up to it), so the
    // dropdown indents them to keep them from reading as separate buckets.
    { value: 'positive', label: 'Profit (≥ 0)', nested: true },
    { value: 'negative', label: 'Loss (< 0)', nested: true },
];
const NET_REVENUE_LABELS = Object.fromEntries(NET_REVENUE_OPTIONS.map((o) => [o.value, o.label]));

// Client-side equivalent of the backend's netRevenue filter, for the
// group drill-down view (which filters its already-loaded members locally).
export const matchesNetRevenueFilter = (campaign, filter) => {
    if (!filter || filter === 'All') return true;
    const net = campaign.netRevenue;
    if (filter === 'with') return net != null;
    if (filter === 'without') return net == null;
    if (net == null) return false;
    return filter === 'positive' ? net >= 0 : net < 0;
};

// Same shape as the backend /campaign/filters netRevenueCounts, for a list
// that's already loaded client-side (a group's members).
export const countNetRevenue = (campaigns) =>
    Object.fromEntries(
        NET_REVENUE_OPTIONS.map(({ value }) => [
            value === 'All' ? 'all' : value,
            campaigns.filter((c) => matchesNetRevenueFilter(c, value)).length,
        ])
    );

const describeList = (label, items) => {
    if (items.length === 1) return `${label}: ${items[0]}`;
    if (items.length === 2) return `${label}: ${items[0]}, ${items[1]}`;
    return `${label}: ${items.length} selected`;
};

// Shared by the Budget and Limit chips below — same "min/max/either/neither"
// shape, just a different label and pair of filter keys.
const describeRange = (label, min, max) => {
    if (min && max) return `${label}: ₹${min} - ₹${max}`;
    if (min) return `${label}: ≥ ₹${min}`;
    if (max) return `${label}: ≤ ₹${max}`;
    return label;
};

const buildActiveChips = (filters, setFilter, { hideFy, hideMonth } = {}) => {
    const chips = [];

    if (!hideFy && filters.fy !== 'All') {
        chips.push({
            key: 'fy',
            label: `Year: ${filters.fy}`,
            onDelete: () => setFilter('fy', 'All'),
        });
    }
    if (!hideMonth && filters.month !== 'All') {
        chips.push({
            key: 'month',
            label: `Month: ${MONTH_LABELS[filters.month] || filters.month}`,
            onDelete: () => setFilter('month', 'All'),
        });
    }
    if (filters.category.length) {
        chips.push({
            key: 'category',
            label: describeList('Category', filters.category),
            onDelete: () => setFilter('category', []),
        });
    }
    if (filters.platform.length) {
        chips.push({
            key: 'platform',
            label: describeList('Platform', filters.platform.map((p) => p.name)),
            onDelete: () => setFilter('platform', []),
        });
    }
    if (filters.createdby.length) {
        chips.push({
            key: 'createdby',
            label: describeList('By', filters.createdby.map((c) => c.name)),
            onDelete: () => setFilter('createdby', []),
        });
    }
    if (filters.minBudget || filters.maxBudget) {
        chips.push({
            key: 'budget',
            label: describeRange('Budget', filters.minBudget, filters.maxBudget),
            onDelete: () => setFilter('minBudget', '', 'maxBudget', ''),
        });
    }
    if (filters.minLimit || filters.maxLimit) {
        chips.push({
            key: 'limit',
            label: describeRange('Limit', filters.minLimit, filters.maxLimit),
            onDelete: () => setFilter('minLimit', '', 'maxLimit', ''),
        });
    }
    if (filters.netRevenue && filters.netRevenue !== 'All') {
        chips.push({
            key: 'netRevenue',
            label: `Net Revenue: ${NET_REVENUE_LABELS[filters.netRevenue] || filters.netRevenue}`,
            onDelete: () => setFilter('netRevenue', 'All'),
        });
    }

    return chips;
};

const CampaignFilters = React.memo(function CampaignFilters({
    filters,
    onFiltersChange,
    filterOptions,
    hideFy = false,
    hideMonth = false,
    showNetRevenue = false,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    // Supports the budget chip's delete handler, which needs to clear two
    // keys (min and max) in a single state update.
    const setFilter = (key, value, key2, value2) =>
        onFiltersChange(
            key2
                ? { ...filters, [key]: value, [key2]: value2 }
                : { ...filters, [key]: value }
        );

    const clearFilters = () =>
        onFiltersChange({
            fy: 'All',
            month: 'All',
            category: [],
            platform: [],
            createdby: [],
            minBudget: '',
            maxBudget: '',
            minLimit: '',
            maxLimit: '',
            netRevenue: 'All',
        });

    const activeChips = buildActiveChips(filters, setFilter, { hideFy, hideMonth });

    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            rowGap={1}
            justifyContent="center"
        >
            <Button
                variant="outlined"
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                startIcon={
                    <Badge
                        badgeContent={activeChips.length}
                        color="primary"
                        sx={{ '& .MuiBadge-badge': { right: -4, top: 2 } }}
                    >
                        <FilterList fontSize="small" />
                    </Badge>
                }
            >
                Filters
            </Button>

            {activeChips.map((chip) => (
                <Chip
                    key={chip.key}
                    label={chip.label}
                    size="small"
                    onDelete={chip.onDelete}
                />
            ))}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box sx={{ p: 2.5, width: 320 }}>
                    <Stack spacing={2}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Filter Campaigns
                        </Typography>

                        {!hideFy && (
                            <FormControl size="small" fullWidth>
                                <InputLabel id="campaign-fy-label">
                                    Financial Year
                                </InputLabel>
                                <Select
                                    labelId="campaign-fy-label"
                                    label="Financial Year"
                                    value={filters.fy}
                                    onChange={(e) => setFilter('fy', e.target.value)}
                                >
                                    {FINANCIAL_YEARS.map((fy) => (
                                        <MenuItem key={fy} value={fy}>
                                            {fy}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {!hideMonth && (
                            <FormControl size="small" fullWidth>
                                <InputLabel id="campaign-month-label">
                                    Month
                                </InputLabel>
                                <Select
                                    labelId="campaign-month-label"
                                    label="Month"
                                    value={filters.month}
                                    onChange={(e) => setFilter('month', e.target.value)}
                                >
                                    {MONTHS.map(({ value, label }) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <Autocomplete
                            multiple
                            size="small"
                            options={filterOptions.categories}
                            value={filters.category}
                            onChange={(e, value) => setFilter('category', value)}
                            renderInput={(params) => (
                                <TextField {...params} label="Category" placeholder="Any" />
                            )}
                        />

                        <Autocomplete
                            multiple
                            size="small"
                            options={filterOptions.platforms}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={filters.platform}
                            onChange={(e, value) => setFilter('platform', value)}
                            renderInput={(params) => (
                                <TextField {...params} label="Platform" placeholder="Any" />
                            )}
                        />

                        <Autocomplete
                            multiple
                            size="small"
                            options={filterOptions.creators}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={filters.createdby}
                            onChange={(e, value) => setFilter('createdby', value)}
                            renderInput={(params) => (
                                <TextField {...params} label="Created By" placeholder="Any" />
                            )}
                        />

                        <Stack direction="row" spacing={1.5}>
                            <TextField
                                size="small"
                                type="number"
                                label="Min Budget"
                                fullWidth
                                value={filters.minBudget}
                                onChange={(e) => setFilter('minBudget', e.target.value)}
                                placeholder={String(filterOptions.budgetRange.min ?? 0)}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Max Budget"
                                fullWidth
                                value={filters.maxBudget}
                                onChange={(e) => setFilter('maxBudget', e.target.value)}
                                placeholder={String(filterOptions.budgetRange.max ?? 0)}
                            />
                        </Stack>

                        <Stack direction="row" spacing={1.5}>
                            <TextField
                                size="small"
                                type="number"
                                label="Min Limit"
                                fullWidth
                                value={filters.minLimit}
                                onChange={(e) => setFilter('minLimit', e.target.value)}
                                placeholder={String(filterOptions.limitRange.min ?? 0)}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Max Limit"
                                fullWidth
                                value={filters.maxLimit}
                                onChange={(e) => setFilter('maxLimit', e.target.value)}
                                placeholder={String(filterOptions.limitRange.max ?? 0)}
                            />
                        </Stack>

                        {showNetRevenue && (
                            <FormControl size="small" fullWidth>
                                <InputLabel id="campaign-net-revenue-label">
                                    Net Revenue
                                </InputLabel>
                                <Select
                                    labelId="campaign-net-revenue-label"
                                    label="Net Revenue"
                                    value={filters.netRevenue || 'All'}
                                    onChange={(e) => setFilter('netRevenue', e.target.value)}
                                >
                                    {NET_REVENUE_OPTIONS.map(({ value, label, nested }) => {
                                        const count = filterOptions.netRevenueCounts?.[value === 'All' ? 'all' : value];
                                        return (
                                            <MenuItem key={value} value={value} sx={nested ? { pl: 4 } : undefined}>
                                                {nested ? '↳ ' : ''}
                                                {count != null ? `${label} (${count})` : label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        )}

                        <Divider />

                        <Stack direction="row" justifyContent="space-between">
                            <Button
                                size="small"
                                color="secondary"
                                startIcon={<FilterAltOff />}
                                disabled={!activeChips.length}
                                onClick={clearFilters}
                            >
                                Clear all
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => setAnchorEl(null)}
                            >
                                Done
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Popover>
        </Stack>
    );
});

export default CampaignFilters;
